/**
 * TeamConfigLayout: the /employees/teams/:teamId layout route, the staff
 * counterpart of SettingsLayout. The same frame, with the team's name as
 * the title, a link back to the team list, and one link per
 * TEAM_CONFIG_SECTIONS entry. The team comes from the staff team list,
 * which the Employees page has usually just fetched, and reaches the
 * pages through the outlet context so each renders with it in hand.
 *
 * Entering the area ends impersonation. Impersonation is the read-only
 * view of a customer's workspace; configuring a team is a staff action
 * done as oneself, so the two never overlap on screen. The layout drops
 * this tab's target and reloads (TeamContext.stopImpersonation), which
 * re-scopes every cached query, and renders nothing until then. Covers a
 * Configure press, a deep link, and a tab opened from the row alike.
 * Other tabs keep impersonating (lib/tabScope.ts).
 *
 * TeamConfigSectionRoute is the element every page route renders through.
 * It keys the page on the team id, so a page never carries one team's
 * form state into another.
 */

import { useEffect } from 'react'
import { ChevronLeft, Shield } from 'lucide-react'
import { Link, Outlet, useOutletContext, useParams } from 'react-router-dom'

import { getApiErrorDetail } from '@/api/client'
import type { ImpersonatableTeam } from '@/api/generated/model'
import { SettingsFrame, type SettingsNavGroup } from '@/components/settings/SettingsFrame'
import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { EYEBROW } from '@/components/ui/eyebrow'
import { PageContainer } from '@/components/ui/page-container'
import { Skeleton } from '@/components/ui/skeleton'
import { useTeam } from '@/contexts/TeamContext'
import { useInternalTeams } from '@/hooks/useInternalTeams'

import { EMPLOYEES_ROOT, teamConfigPath } from './paths'
import {
  TEAM_CONFIG_GROUP_LABEL,
  TEAM_CONFIG_SECTIONS,
  type TeamConfigSection,
} from './sections'

interface TeamConfigOutletContext {
  team: ImpersonatableTeam
}

export function TeamConfigLayout(): React.JSX.Element | null {
  const { teamId } = useParams()
  const { impersonatingTeamId, stopImpersonation } = useTeam()
  const impersonating = impersonatingTeamId !== null
  // A browser side effect (localStorage, then a reload), so an effect.
  useEffect(() => {
    if (impersonating) stopImpersonation()
  }, [impersonating, stopImpersonation])
  const teamsQuery = useInternalTeams({ enabled: !impersonating })

  if (impersonating) return null
  if (teamsQuery.isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-24 w-full" />
      </PageContainer>
    )
  }
  if (teamsQuery.isError) {
    return (
      <PageContainer>
        <p className="text-sm text-destructive" role="alert">
          {getApiErrorDetail(teamsQuery.error) ?? 'The team list failed to load.'}
        </p>
      </PageContainer>
    )
  }
  const team = teamsQuery.data?.teams.find((candidate) => candidate.id === teamId)
  if (team === undefined) {
    return (
      <PageContainer>
        <EmptyState
          icon={Shield}
          title="Team not found"
          description="No team has this id. It may have been deleted."
          action={
            <Button variant="outline" asChild>
              <Link to={EMPLOYEES_ROOT}>Back to teams</Link>
            </Button>
          }
        />
      </PageContainer>
    )
  }
  return (
    <SettingsFrame
      navLabel="Team configuration"
      title={team.name}
      subtitle={team.slug}
      lead={<BackToTeams />}
      groups={navGroups(team.id)}
    >
      <Outlet context={{ team } satisfies TeamConfigOutletContext} />
    </SettingsFrame>
  )
}

function navGroups(teamId: string): SettingsNavGroup[] {
  return [
    {
      label: TEAM_CONFIG_GROUP_LABEL,
      entries: TEAM_CONFIG_SECTIONS.map((section) => ({
        to: teamConfigPath(teamId, section.path),
        label: section.label,
        icon: section.icon,
      })),
    },
  ]
}

function BackToTeams(): React.JSX.Element {
  return (
    <Link
      to={EMPLOYEES_ROOT}
      className={`${EYEBROW} inline-flex items-center gap-1 transition-colors hover:text-foreground`}
    >
      <ChevronLeft className="h-3 w-3" aria-hidden="true" />
      Teams
    </Link>
  )
}

export function TeamConfigSectionRoute({
  section,
}: {
  section: TeamConfigSection
}): React.JSX.Element {
  const { team } = useOutletContext<TeamConfigOutletContext>()
  const Component = section.Component
  return <Component key={team.id} section={section} team={team} />
}
